import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { theme } from "../../theme";

/**
 * OfflineIndicator - Shows a banner when the user loses internet connection.
 * Uses navigator.onLine + window online/offline events.
 * Renders via portal to document.body so it works regardless of screen routing.
 *
 * Icelandic text:
 *   Offline:  "Engin nettenging"          (No internet connection)
 *   Restored: "Nettenging endurheimt"      (Connection restored)
 */
export default function OfflineIndicator() {
  const [isOnline, setIsOnline] = useState(() => navigator.onLine);
  const [showRestored, setShowRestored] = useState(false);
  const [visible, setVisible] = useState(!navigator.onLine);

  useEffect(() => {
    let restoredTimer = null;

    const handleOnline = () => {
      setIsOnline(true);
      setShowRestored(true);
      setVisible(true);
      restoredTimer = setTimeout(() => {
        setShowRestored(false);
        setVisible(false);
      }, 3000);
    };

    const handleOffline = () => {
      setIsOnline(false);
      setShowRestored(false);
      setVisible(true);
      if (restoredTimer) clearTimeout(restoredTimer);
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      if (restoredTimer) clearTimeout(restoredTimer);
    };
  }, []);

  if (!visible) return null;

  const isOffline = !isOnline;

  const bannerStyle = {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    zIndex: 9999,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    padding: "12px 16px",
    fontFamily: "'DM Sans', 'Segoe UI', system-ui, sans-serif",
    fontSize: 14,
    fontWeight: 600,
    color: theme.text,
    background: isOffline
      ? "linear-gradient(135deg, #7f1d1d 0%, #991b1b 50%, #7f1d1d 100%)"
      : "linear-gradient(135deg, #064e3b 0%, #065f46 50%, #064e3b 100%)",
    borderBottom: isOffline
      ? "2px solid #dc2626"
      : `2px solid ${theme.green}`,
    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.5)",
    animation: "offlineSlideDown 0.35s cubic-bezier(0.22, 1, 0.36, 1)",
  };

  const iconStyle = {
    fontSize: 16,
    lineHeight: 1,
  };

  const banner = (
    <>
      <style>{`
        @keyframes offlineSlideDown {
          from {
            transform: translateY(-100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
      `}</style>
      <div role="alert" aria-live="assertive" style={bannerStyle}>
        <span style={iconStyle}>{isOffline ? "\uD83D\uDCE1" : "\u2705"}</span>
        <span>
          {isOffline ? "Engin nettenging" : "Nettenging endurheimt"}
        </span>
      </div>
    </>
  );

  return createPortal(banner, document.body);
}
